import { motion } from "framer-motion";
import { VerifiedStudentIcon, HandshakeIcon, ShieldIcon, BellIcon } from "../lib/icons";
import { TILE_BOUNCE, useTapBounce } from "../lib/tapBounce";

const TRUST = [
  {
    icon: VerifiedStudentIcon,
    tile: "bg-cobalt",
    name: "Verified Students",
    body: "Every student is confirmed as enrolled.",
  },
  {
    icon: HandshakeIcon,
    tile: "bg-blossom",
    name: "Visible Impact",
    body: "Tax-deductible gifts to My Rich Aunt fund grants for verified students with approved campaigns.",
  },
  {
    icon: ShieldIcon,
    tile: "bg-yellow",
    glyph: "text-ink",
    name: "Secure Platform",
    body: "Payments run on Stripe, a payment processor used by DonorsChoose, Go Fund Me, and Peloton.",
  },
  {
    icon: BellIcon,
    tile: "bg-grass",
    name: "Progress Updates",
    body: "Regular updates as My Rich Aunt funds each campaign thanks to donors like you.",
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const TrustPoint = ({ t }) => {
  const Icon = t.icon;
  const [state, onTap] = useTapBounce();
  return (
    <motion.div
      initial="rest"
      whileHover="hover"
      animate={state}
      onTap={onTap}
      className="group flex items-center gap-4 rounded-xl bg-bone p-3.5 shadow-[0_2px_10px_-4px_rgba(26,23,20,0.08)]"
      data-testid={`trust-point-${slug(t.name)}`}
    >
      <motion.div
        className={`tile-clay flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${t.tile}`}
        variants={{ rest: { y: 0, scale: 1 }, hover: { y: -4, scale: 1.04 }, bounce: TILE_BOUNCE }}
        transition={{ type: "spring", stiffness: 420, damping: 14, mass: 0.6 }}
        data-testid={`trust-point-${slug(t.name)}-tile`}
      >
        <Icon className={`h-6 w-6 ${t.glyph || "text-bone"}`} />
      </motion.div>
      <div className="min-w-0">
        <h4 className="font-serif text-3xl font-bold leading-tight text-ink">{t.name}</h4>
        <p className="mt-0.5 text-[13px] leading-snug text-olive">{t.body}</p>
      </div>
    </motion.div>
  );
};

/**
 * The 4-point "100% Verified" trust card.
 * Nested white cards with solid colored tiles; titles stay ink for contrast.
 * Reusable across the Impact and Footer sections.
 */
export const TrustCard = ({ className = "" }) => (
  <div
    className={`relative flex flex-col rounded-2xl border-2 border-cobalt bg-cobalt/[0.04] p-4 sm:p-5 ${className}`}
    data-testid="trust-card"
  >
    <div
      className="absolute -right-4 -top-4 z-10 rotate-[-8deg] rounded-full bg-tangerine px-4 py-1.5 text-[13px] font-bold uppercase tracking-wide text-bone shadow-lg"
      data-testid="trust-verified-badge"
    >
      100% Verified
    </div>

    <div className="flex flex-col gap-3">
      {TRUST.map((t) => <TrustPoint key={t.name} t={t} />)}
    </div>
  </div>
);
